import type { ReactNode } from 'react';
import { useTheme, type DefaultTheme } from 'styled-components';
import type { Security } from '../../types/Security';
import { Box, Flex, type BoxProps } from '../Core';
import { CurrencyIcon } from '../CurrencyIcon';
import { EllipsisBoxWithTooltip } from '../Form/BaseSelect';
import { Wrapper } from './styles';

type SecurityProps = (
  | {
      symbol?: string;
      security: Security;
    }
  | {
      symbol: string;
      security?: Security;
    }
) & {
  currency?: string;
  showIcon?: boolean;
  showDescription?: boolean;
  theme?: DefaultTheme;
  iconSize?: number;
  colorful?: boolean;
  securityTooltip?: ReactNode;
  suffix?: ReactNode;
} & Omit<BoxProps, 'security'>;

export function SecurityRenderer({
  symbol,
  security,
  currency,
  showIcon,
  showDescription,
  theme,
  iconSize = 16,
  colorful,
  securityTooltip,
  suffix,
  ...props
}: SecurityProps) {
  const hookTheme = useTheme();
  const themeToUse = theme ?? hookTheme;
  const { colorTextSubtle, colorTextImportant } = themeToUse;

  const displaySymbol = security?.DisplaySymbol || symbol;

  return (
    <Wrapper gap="spacing4" color="colorTextImportant" {...props}>
      {showIcon !== false && (currency != null || security != null) && (
        <CurrencyIcon
          size={iconSize}
          currency={currency || security?.BaseCurrency || symbol?.split('-')[0]}
          theme={themeToUse}
          colorful={colorful}
          style={ICON_STYLE}
        />
      )}

      <Box overflow="hidden" flex="1 1 0" textAlign="left">
        <Flex color={colorTextImportant}>
          <EllipsisBoxWithTooltip
            alwaysShowTooltip={!!securityTooltip}
            tooltipProps={{
              placement: 'top',
              tooltip: securityTooltip || displaySymbol,
            }}
          >
            {displaySymbol}
          </EllipsisBoxWithTooltip>
          {
            /* + means that additional information can be seen by hovering symbol */
            !!securityTooltip && '+'
          }
          {suffix && <Box ml="spacing8">{suffix}</Box>}
        </Flex>
        {showDescription && (
          <Flex
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            w="100%"
            color={colorTextSubtle}
            fontSize="fontSizeTiny"
            gap="spacing4"
          >
            {security?.Description}
          </Flex>
        )}
      </Box>
    </Wrapper>
  );
}

const ICON_STYLE = {
  flexShrink: 0,
};
